import { useState, useEffect } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import { getUserRole } from '../services/adminService';

interface UseInstanceRoleReturn {
  isAdmin: boolean;
  role: string | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// Helper to get cached role from sessionStorage
const getCachedRole = (userId: string): string | null => {
  try {
    const cached = sessionStorage.getItem(`user-role-${userId}`);
    if (cached) {
      const parsed = JSON.parse(cached);
      // Check if cache is still valid (1 hour)
      if (parsed.timestamp && Date.now() - parsed.timestamp < 3600000) {
        return parsed.role;
      }
    }
  } catch (error) {
    console.error('Failed to parse cached role:', error);
  }
  return null;
};

// Helper to cache role in sessionStorage
const cacheRole = (userId: string, role: string) => {
  try {
    sessionStorage.setItem(`user-role-${userId}`, JSON.stringify({
      role,
      timestamp: Date.now()
    }));
  } catch (error) {
    console.error('Failed to cache role:', error);
  }
};

/**
 * Custom hook to get the user's role for the current instance
 * This ensures admin privileges are instance-specific, not global
 */
export const useInstanceRole = (): UseInstanceRoleReturn => {
  const { user, isLoaded } = useUser();
  const { getToken } = useAuth();
  
  // Initialize with cached role if available
  const cachedRole = user?.id ? getCachedRole(user.id) : null;
  const [role, setRole] = useState<string | null>(cachedRole);
  const [loading, setLoading] = useState(!cachedRole); // Only load if no cache
  const [error, setError] = useState<string | null>(null);

  const fetchRole = async () => {
    if (!user?.id || !isLoaded) {
      setLoading(false);
      return;
    }

    try {
      // If we have a cached role, don't show loading state
      if (!getCachedRole(user.id)) {
        setLoading(true);
      }
      setError(null);
      
      // Get authentication token
      const token = await getToken() || null;
      
      // Fetch role from local database for this instance
      const userRole = await getUserRole(user.id, token);
      
      if (userRole === 'not_found' || userRole === 'unknown') {
        setRole('USER'); // Default to USER if not found
        cacheRole(user.id, 'USER');
      } else {
        const normalizedRole = typeof userRole === 'string' ? userRole.toUpperCase() : 'USER';
        setRole(normalizedRole);
        cacheRole(user.id, normalizedRole);
      }
    } catch (err) {
      console.error('Error fetching user role:', err);
      setError('Failed to fetch user role');
      setRole('USER'); // Default to USER on error
      // Don't cache errors
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRole();
  }, [user?.id, isLoaded]);

  return {
    isAdmin: role === 'ADMIN',
    role,
    loading,
    error,
    refetch: fetchRole
  };
}; 